import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { descarregaAuditoria, useAuditoria, type FiltresAuditoria } from '@/api/informes';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { CanvisAuditoria } from '@/pages/administracio/CanvisAuditoria';
import { Avatar, Buit } from '@/pages/operacio/comu';
import { XipSelect } from './comu';
import {
  FILTRES_AUDITORIA,
  RESULTATS_AUDITORIA,
  useEtiquetaAccioAuditoria,
  useEtiquetaEntitatAuditoria,
  type FiltreAuditoria,
} from './suport';
import './informes.css';

const MIDA = 25;

function BadgeResultat({ resultat }: { resultat: string }) {
  const { t } = useTranslation('informes');
  const text = t(`w44.resultats.${resultat}`, { defaultValue: resultat });
  if (resultat === 'OK') {
    return (
      <span className="badge badge--ok">
        <Icona nom="checkmark-circle" />
        {text}
      </span>
    );
  }
  if (resultat === 'DENEGAT') {
    return (
      <span className="badge badge--gray">
        <Icona nom="lock" />
        {text}
      </span>
    );
  }
  return (
    <span className="badge badge--critic">
      <Icona nom="xmark-circle" />
      {text}
    </span>
  );
}

export function W44Auditoria() {
  const { t } = useTranslation('informes');
  const f = useFormat();
  const etiquetaAccio = useEtiquetaAccioAuditoria();
  const etiquetaEntitat = useEtiquetaEntitatAuditoria();
  const [filtre, setFiltre] = useState<FiltreAuditoria>('TOTS');
  const [resultat, setResultat] = useState('');
  const [text, setText] = useState('');
  const [page, setPage] = useState(0);
  const [desde] = useState(() => new Date(Date.now() - 30 * 86_400_000).toISOString());
  const filtres: FiltresAuditoria = {
    desde,
    accio: FILTRES_AUDITORIA[filtre] ?? undefined,
    resultat: resultat || undefined,
    page,
    size: MIDA,
  };
  const auditoria = useAuditoria(filtres);
  const total = auditoria.data?.total ?? 0;
  const pagines = Math.max(1, Math.ceil(total / MIDA));
  const cerca = text.trim().toLowerCase();
  // La cerca filtra la pàgina carregada (persona, acció, objecte o IP).
  const items = (auditoria.data?.items ?? []).filter(
    (r) =>
      !cerca ||
      [r.upn, r.accio, etiquetaAccio(r.accio, r.entitat), r.entitat, r.entitatId, r.ip]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(cerca)),
  );

  return (
    <section className="pantalla" data-screen="W-44">
      <Toolbar titol={t('titols.auditoria')}>
        <label className="search" style={{ width: 220, margin: 0 }}>
          <Icona nom="magnifier" />
          <input
            type="search"
            value={text}
            placeholder={t('w44.cerca')}
            aria-label={t('w44.cerca')}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn--s"
          onClick={() => void descarregaAuditoria(filtres)}
        >
          <Icona nom="doc-export" />
          {t('comu.exporta')}
        </button>
      </Toolbar>
      <div className="content">
        <div className="filtres-informe" style={{ marginBottom: 14 }}>
          <div className="chips" role="group" aria-label={t('w44.filtre')}>
            {(Object.keys(FILTRES_AUDITORIA) as FiltreAuditoria[]).map((k) => (
              <button
                key={k}
                type="button"
                className="chip"
                aria-pressed={filtre === k}
                onClick={() => {
                  setFiltre(k);
                  setPage(0);
                }}
              >
                {t(`w44.filtres.${k}`)}
              </button>
            ))}
          </div>
          <div className="spacer" />
          <XipSelect
            icona="shield-check"
            etiqueta={t('w44.resultat')}
            valor={resultat}
            onCanvia={(v) => {
              setResultat(v);
              setPage(0);
            }}
            opcions={[
              { valor: '', etiqueta: t('w44.totsResultats') },
              ...RESULTATS_AUDITORIA.map((r) => ({
                valor: String(r),
                etiqueta: t(`w44.resultats.${r}`),
              })),
            ]}
          />
          <span className="chip" aria-disabled="true" style={{ cursor: 'default' }}>
            <Icona nom="calendar" />
            {t('w44.darrers30')}
          </span>
        </div>
        {auditoria.isError ? (
          <ErrorCarrega onReintenta={() => void auditoria.refetch()} />
        ) : (
          <div className="panel">
            {items.length === 0 ? (
              <Buit icona="clipboard">
                {auditoria.data ? t('comu.senseDades') : t('comu.carregant')}
              </Buit>
            ) : (
              <table className="taula" data-testid="auditoria">
                <thead>
                  <tr>
                    <th>{t('w44.persona')}</th>
                    <th>{t('w44.data')}</th>
                    <th>{t('w44.accio')}</th>
                    <th>{t('w44.objecte')}</th>
                    <th>{t('w44.resultat')}</th>
                    <th>{t('w44.ip')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="hstack gap-8">
                          {r.upn ? (
                            <Avatar nom={r.upn.split('@')[0]?.replace(/[._]/g, ' ')} />
                          ) : (
                            <span className="avatar bg-gray">
                              <Icona nom="shield-check" />
                            </span>
                          )}
                          <span>{r.upn ?? t('w44.sistema')}</span>
                        </div>
                      </td>
                      <td className="mono t-caption nowrap">{f.dataHora(r.at)}</td>
                      <td title={r.accio}>{etiquetaAccio(r.accio, r.entitat)}</td>
                      <td>
                        {etiquetaEntitat(r.entitat)}
                        {r.entitatId ? (
                          <span className="dim mono t-caption"> #{r.entitatId}</span>
                        ) : null}
                        {r.resultat === 'OK' ? (
                          <CanvisAuditoria valorPrevi={r.valorPrevi} valorNou={r.valorNou} compacte />
                        ) : null}
                      </td>
                      <td>
                        <BadgeResultat resultat={r.resultat} />
                        {r.codiError ? (
                          <div className="t-caption-2 dim mono mt-4">{r.codiError}</div>
                        ) : null}
                      </td>
                      <td className="mono t-caption">{r.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="panel__foot">
              <span className="t-caption dim">{t('w44.total', { count: total })}</span>
              <div className="spacer" />
              <button
                type="button"
                className="btn btn--xs"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('w44.anterior')}
              </button>
              <span className="t-caption dim">
                {t('w44.pagina', { n: page + 1, total: pagines })}
              </span>
              <button
                type="button"
                className="btn btn--xs"
                disabled={page + 1 >= pagines}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('w44.seguent')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
